import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "../assets/css/material-kit.css";

const PrivacyPolicy = () => {
  const { i18n } = useTranslation();
  const [currentLang, setCurrentLang] = useState(i18n.language || 'en');

  const changeLanguage = (lang: string) => {
    setCurrentLang(lang);
    i18n.changeLanguage(lang);
  };

  return (
    <div className="container my-5">
      <div className="row">
        <div className="col-lg-10 mx-auto">
          {/* Language Disclaimer */}
          <div className="alert alert-info mb-4">
            <p className="mb-2">
              <strong>
                {currentLang === 'en' && 'This document is available in English, Georgian, and Russian. In case of any discrepancy, the English version shall prevail.'}
                {currentLang === 'ka' && 'ეს დოკუმენტი ხელმისაწვდომია ინგლისურად, ქართულად და რუსულად. გაურკვევლობის შემთხვევაში, უპირატესობა მიენიჭება ინგლისურ ვერსიას.'}
                {currentLang === 'ru' && 'Этот документ доступен на английском, грузинском и русском языках. В случае расхождений приоритет имеет английская версия.'}
              </strong>
            </p>
            <div className="btn-group btn-group-sm" role="group">
              <button 
                type="button" 
                className={`btn ${currentLang === 'en' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => changeLanguage('en')}
              >
                English
              </button>
              <button 
                type="button" 
                className={`btn ${currentLang === 'ka' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => changeLanguage('ka')}
              >
                ქართული
              </button>
              <button 
                type="button" 
                className={`btn ${currentLang === 'ru' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => changeLanguage('ru')}
              >
                Русский
              </button>
            </div>
          </div>

          <h1 className="mb-4">
            {currentLang === 'en' && 'Privacy Policy'}
            {currentLang === 'ka' && 'კონფიდენციალურობის პოლიტიკა'}
            {currentLang === 'ru' && 'Политика конфиденциальности'}
          </h1>
          <p className="text-muted">
            <strong>
              {currentLang === 'en' && 'Last Updated:'}
              {currentLang === 'ka' && 'ბოლო განახლება:'}
              {currentLang === 'ru' && 'Последнее обновление:'}
            </strong> January 1, 2025
          </p>

          {currentLang === 'en' && (
            <>
              <section className="mb-4">
                <h2>1. Introduction</h2>
                <p>
                  At SyncOrb-AI, we are committed to protecting your privacy and ensuring the security of 
                  your personal information. This Privacy Policy explains how we collect, use, disclose, 
                  and safeguard your information when you use our services.
                </p>
              </section>

              <section className="mb-4">
                <h2>2. Information We Collect</h2>
                <h3 className="h5 mt-3">Personal Information</h3>
                <p>We may collect personal information that you provide to us, including:</p>
                <ul>
                  <li>Name and contact information (email, phone number)</li>
                  <li>Account credentials and profile information</li>
                  <li>Payment and billing information</li>
                  <li>Communication preferences</li>
                </ul>

                <h3 className="h5 mt-3">Usage Information</h3>
                <p>We automatically collect certain information about your device and how you interact with our services:</p>
                <ul>
                  <li>Device information (IP address, browser type, operating system)</li>
                  <li>Usage data (pages visited, features used, time spent)</li>
                  <li>Cookies and similar tracking technologies</li>
                  <li>Log files and analytics data</li>
                </ul>

                <h3 className="h5 mt-3">AI Training Data</h3>
                <p>
                  When you use our AI services, we may collect data to improve our models and services. 
                  This data is anonymized and aggregated wherever possible.
                </p>
              </section>

              <section className="mb-4">
                <h2>3. How We Use Your Information</h2>
                <p>We use the collected information for the following purposes:</p>
                <ul>
                  <li>To provide, maintain, and improve our services</li>
                  <li>To process transactions and send related information</li>
                  <li>To respond to your inquiries and provide customer support</li>
                  <li>To send administrative information and updates</li>
                  <li>To train and improve our AI models and algorithms</li>
                  <li>To detect, prevent, and address technical issues and security threats</li>
                  <li>To comply with legal obligations and enforce our terms</li>
                  <li>To analyze usage patterns and optimize user experience</li>
                </ul>
              </section>

              <section className="mb-4">
                <h2>4. Data Sharing and Disclosure</h2>
                <p>We may share your information in the following circumstances:</p>
                <ul>
                  <li><strong>Service Providers:</strong> With third-party vendors who perform services on our behalf</li>
                  <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
                  <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                  <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
                </ul>
                <p>We do not sell your personal information to third parties.</p>
              </section>

              <section className="mb-4">
                <h2>5. Data Security</h2>
                <p>
                  We implement appropriate technical and organizational measures to protect your personal 
                  information against unauthorized access, alteration, disclosure, or destruction. These 
                  measures include:
                </p>
                <ul>
                  <li>Encryption of data in transit and at rest</li>
                  <li>Regular security assessments and audits</li>
                  <li>Access controls and authentication mechanisms</li>
                  <li>Employee training on data protection practices</li>
                </ul>
                <p>
                  However, no method of transmission over the internet is 100% secure, and we cannot 
                  guarantee absolute security.
                </p>
              </section>

              <section className="mb-4">
                <h2>6. Data Retention</h2>
                <p>
                  We retain your personal information for as long as necessary to fulfill the purposes 
                  outlined in this Privacy Policy, unless a longer retention period is required or permitted 
                  by law. When we no longer need your information, we will securely delete or anonymize it.
                </p>
              </section>

              <section className="mb-4">
                <h2>7. Your Rights and Choices</h2>
                <p>Depending on your location, you may have the following rights:</p>
                <ul>
                  <li><strong>Access:</strong> Request access to your personal information</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                  <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
                  <li><strong>Objection:</strong> Object to certain processing of your information</li>
                  <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                </ul>
                <p>To exercise these rights, please contact us using the information provided below.</p>
              </section>

              <section className="mb-4">
                <h2>8. Cookies and Tracking Technologies</h2>
                <p>
                  We use cookies and similar technologies to enhance your experience, analyze usage, and 
                  deliver personalized content. You can control cookie settings through your browser, but 
                  disabling cookies may affect the functionality of our services.
                </p>
              </section>

              <section className="mb-4">
                <h2>9. International Data Transfers</h2>
                <p>
                  Your information may be transferred to and processed in countries other than your country 
                  of residence. We ensure appropriate safeguards are in place to protect your information 
                  in accordance with this Privacy Policy and applicable laws.
                </p>
              </section>

              <section className="mb-4">
                <h2>10. Children's Privacy</h2>
                <p>
                  Our services are not intended for individuals under the age of 13 (or 16 in the European 
                  Economic Area). We do not knowingly collect personal information from children. If you 
                  believe we have collected information from a child, please contact us immediately.
                </p>
              </section>

              <section className="mb-4">
                <h2>11. Changes to This Privacy Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of any material 
                  changes by posting the new policy on this page and updating the "Last Updated" date. 
                  Your continued use of our services after changes constitutes acceptance of the updated policy.
                </p>
              </section>

              <section className="mb-4">
                <h2>12. Contact Us</h2>
                <p>
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our 
                  data practices, please contact us:
                </p>
                <ul className="list-unstyled">
                  <li><strong>Email:</strong> support@syncorbai.com</li>
                  <li><strong>Address:</strong> SyncOrb-AI, Tbilisi, Georgia</li>
                </ul>
              </section>
            </>
          )}

          {currentLang === 'ka' && (
            <>
              <section className="mb-4">
                <h2>1. შესავალი</h2>
                <p>
                  SyncOrb-AI-ში ჩვენ ვართ ერთგულნი თქვენი კონფიდენციალურობის დაცვისა და თქვენი პერსონალური 
                  ინფორმაციის უსაფრთხოების უზრუნველყოფის მიმართ. ეს კონფიდენციალურობის პოლიტიკა განმარტავს, 
                  თუ როგორ ვაგროვებთ, ვიყენებთ, ვაჟვოვებთ და ვიცავთ თქვენს ინფორმაციას, როდესაც იყენებთ ჩვენს სერვისებს.
                </p>
              </section>

              <section className="mb-4">
                <h2>2. ინფორმაცია, რომელსაც ჩვენ ვაგროვებთ</h2>
                <h3 className="h5 mt-3">პერსონალური ინფორმაცია</h3>
                <p>ჩვენ შეგვიძლია შევაგროვოთ პერსონალური ინფორმაცია, რომელსაც თქვენ გვაწვდით, მათ შორის:</p>
                <ul>
                  <li>სახელი და საკონტაქტო ინფორმაცია (ელ. ფოსტა, ტელეფონის ნომერი)</li>
                  <li>ანგარიშის რეკვიზიტები და პროფილის ინფორმაცია</li>
                  <li>გადახდისა და ანგარიშსწორების ინფორმაცია</li>
                  <li>კომუნიკაციის პარამეტრები</li>
                </ul>

                <h3 className="h5 mt-3">გამოყენების ინფორმაცია</h3>
                <p>ჩვენ ავტომატურად ვაგროვებთ გარკვეულ ინფორმაციას თქვენი მოწყობილობისა და თქვენი ჩვენს სერვისებთან ურთიერთქმედების შესახებ:</p>
                <ul>
                  <li>მოწყობილობის ინფორმაცია (IP მისამართი, ბრაუზერის ტიპი, ოპერაციული სისტემა)</li>
                  <li>გამოყენების მონაცემები (მონახულებული გვერდები, გამოყენებული ფუნქციები, დახარჯული დრო)</li>
                  <li>ქუქი-ფაილები და მსგავსი თვალყურის დევნების ტექნოლოგიები</li>
                  <li>ჟურნალის ფაილები და ანალიტიკის მონაცემები</li>
                </ul>

                <h3 className="h5 mt-3">AI ტრენინგის მონაცემები</h3>
                <p>
                  როდესაც იყენებთ ჩვენს AI სერვისებს, ჩვენ შეგვიძლია შევაგროვოთ მონაცემები ჩვენი მოდელების 
                  და სერვისების გასაუმჯობესებლად. ეს მონაცემები ანონიმირებული და აგრეგირებულია, სადაც შესაძლებელია.
                </p>
              </section>

              <section className="mb-4">
                <h2>3. როგორ ვიყენებთ თქვენს ინფორმაციას</h2>
                <p>ჩვენ ვიყენებთ შეგროვებულ ინფორმაციას შემდეგი მიზნებისთვის:</p>
                <ul>
                  <li>ჩვენი სერვისების მიწოდება, შენარჩუნება და გაუმჯობესება</li>
                  <li>ტრანზაქციების დამუშავება და დაკავშირებული ინფორმაციის გაგზავნა</li>
                  <li>თქვენი შეკითხვებზე პასუხის გაცემა და მომხმარებელთა მხარდაჭერის უზრუნველყოფა</li>
                  <li>ადმინისტრაციული ინფორმაციისა და განახლებების გაგზავნა</li>
                  <li>ჩვენი AI მოდელებისა და ალგორითმების ტრენინგი და გაუმჯობესება</li>
                  <li>ტექნიკური საკითხებისა და უსაფრთხოების საფრთხეების გამოვლენა, თავიდან აცილება და მოგვარება</li>
                  <li>იურიდიული ვალდებულებების შესრულება და ჩვენი პირობების აღსრულება</li>
                  <li>გამოყენების შაბლონების ანალიზი და მომხმარებლის გამოცდილების ოპტიმიზაცია</li>
                </ul>
              </section>

              <section className="mb-4">
                <h2>4. მონაცემთა გაზიარება და გამჟღავნება</h2>
                <p>ჩვენ შეგვიძლია გავუზიაროთ თქვენი ინფორმაცია შემდეგ გარემოებებში:</p>
                <ul>
                  <li><strong>სერვის პროვაიდერები:</strong> მესამე მხარის მიმწოდებლებთან, რომლებიც ასრულებენ სერვისებს ჩვენი სახელით</li>
                  <li><strong>ბიზნეს გადაცემები:</strong> შერწყმებთან, შეძენებთან ან აქტივების გაყიდვასთან დაკავშირებით</li>
                  <li><strong>იურიდიული მოთხოვნები:</strong> როდესაც ეს კანონით არის მოთხოვნილი ან ჩვენი უფლებების დასაცავად</li>
                  <li><strong>თქვენი თანხმობით:</strong> როდესაც თქვენ ცალსახად გვაძლევთ ნებართვას თქვენი ინფორმაციის გასაზიარებლად</li>
                </ul>
                <p>ჩვენ არ ვყიდით თქვენს პერსონალურ ინფორმაციას მესამე მხარეებზე.</p>
              </section>

              <section className="mb-4">
                <h2>5. მონაცემთა უსაფრთხოება</h2>
                <p>
                  ჩვენ ვახორციელებთ შესაბამის ტექნიკურ და ორგანიზაციულ ზომებს თქვენი პერსონალური 
                  ინფორმაციის დასაცავად უნებართვო წვდომისგან, ცვლილებისგან, გამჟღავნებისა და განადგურებისგან. 
                  ეს ზომები მოიცავს:
                </p>
                <ul>
                  <li>მონაცემთა დაშიფვრა გადაცემისა და შენახვის დროს</li>
                  <li>რეგულარული უსაფრთხოების შეფასებები და აუდიტები</li>
                  <li>წვდომის კონტროლი და ავთენტიფიკაციის მექანიზმები</li>
                  <li>თანამშრომელთა ტრენინგი მონაცემთა დაცვის პრაქტიკაში</li>
                </ul>
                <p>
                  თუმცა, ინტერნეტით გადაცემის არცერთი მეთოდი არ არის 100% უსაფრთხო და ჩვენ ვერ 
                  გარანტირებთ აბსოლუტურ უსაფრთხოებას.
                </p>
              </section>

              <section className="mb-4">
                <h2>6. მონაცემთა შენახვა</h2>
                <p>
                  ჩვენ ვინახავთ თქვენს პერსონალურ ინფორმაციას იმდენი ხნის განმავლობაში, რამდენიც 
                  საჭიროა ამ კონფიდენციალურობის პოლიტიკაში აღწერილი მიზნების შესასრულებლად, თუ 
                  უფრო ხანგრძლივი შენახვის პერიოდი არ არის მოთხოვნილი ან დაშვებული კანონით. როდესაც 
                  აღარ გვჭირდება თქვენი ინფორმაცია, ჩვენ უსაფრთხოდ წავშლით ან ანონიმიზაცია მოვუხდენთ მას.
                </p>
              </section>

              <section className="mb-4">
                <h2>7. თქვენი უფლებები და არჩევანი</h2>
                <p>თქვენი მდებარეობიდან გამომდინარე, თქვენ შეგიძლიათ გქონდეთ შემდეგი უფლებები:</p>
                <ul>
                  <li><strong>წვდომა:</strong> მოითხოვოთ წვდომა თქვენს პერსონალურ ინფორმაციაზე</li>
                  <li><strong>გამოსწორება:</strong> მოითხოვოთ არაზუსტი ან არასრული მონაცემების გამოსწორება</li>
                  <li><strong>წაშლა:</strong> მოითხოვოთ თქვენი პერსონალური ინფორმაციის წაშლა</li>
                  <li><strong>პორტაბელურობა:</strong> მოითხოვოთ თქვენი მონაცემების ასლი პორტაბელურ ფორმატში</li>
                  <li><strong>წინააღმდეგობა:</strong> წინააღმდეგობა გამოთქვათ თქვენი ინფორმაციის გარკვეულ დამუშავებაზე</li>
                  <li><strong>გამოწერის გაუქმება:</strong> გამოწერის გაუქმება მარკეტინგული კომუნიკაციებიდან</li>
                </ul>
                <p>ამ უფლებების გამოსაყენებლად, გთხოვთ დაგვიკავშირდეთ ქვემოთ მოცემული ინფორმაციის გამოყენებით.</p>
              </section>

              <section className="mb-4">
                <h2>8. ქუქი-ფაილები და თვალყურის დევნების ტექნოლოგიები</h2>
                <p>
                  ჩვენ ვიყენებთ ქუქი-ფაილებსა და მსგავს ტექნოლოგიებს თქვენი გამოცდილების გასაუმჯობესებლად, 
                  გამოყენების გასაანალიზებლად და პერსონალიზებული კონტენტის მისაწოდებლად. თქვენ შეგიძლიათ 
                  კონტროლი მოახდინოთ ქუქი-ფაილების პარამეტრებზე თქვენი ბრაუზერის მეშვეობით, მაგრამ ქუქი-ფაილების 
                  გამორთვამ შეიძლება იმოქმედოს ჩვენი სერვისების ფუნქციონალობაზე.
                </p>
              </section>

              <section className="mb-4">
                <h2>9. საერთაშორისო მონაცემთა გადაცემა</h2>
                <p>
                  თქვენი ინფორმაცია შეიძლება გადაეცეს და დამუშავდეს თქვენი რეზიდენტობის ქვეყნის გარდა სხვა 
                  ქვეყნებში. ჩვენ ვუზრუნველყოფთ შესაბამისი დაცვის ღონისძიებების არსებობას თქვენი ინფორმაციის 
                  დასაცავად ამ კონფიდენციალურობის პოლიტიკისა და გამოყენებადი კანონების შესაბამისად.
                </p>
              </section>

              <section className="mb-4">
                <h2>10. ბავშვთა კონფიდენციალურობა</h2>
                <p>
                  ჩვენი სერვისები არ არის განკუთვნილი 13 წლამდე (ან 16 წლამდე ევროპის ეკონომიკურ არეალში) 
                  ინდივიდებისთვის. ჩვენ ცნობიერად არ ვაგროვებთ პერსონალურ ინფორმაციას ბავშვებისგან. თუ 
                  თქვენ მიგაჩნიათ, რომ ჩვენ შევაგროვეთ ინფორმაცია ბავშვისგან, გთხოვთ დაუყოვნებლივ დაგვიკავშირდეთ.
                </p>
              </section>

              <section className="mb-4">
                <h2>11. ამ კონფიდენციალურობის პოლიტიკის ცვლილებები</h2>
                <p>
                  ჩვენ შეგვიძლია დროდადრო განვაახლოთ ეს კონფიდენციალურობის პოლიტიკა. ჩვენ შეგატყობინებთ 
                  ნებისმიერ მნიშვნელოვან ცვლილებას ახალი პოლიტიკის ამ გვერდზე განთავსებით და "ბოლო განახლების" 
                  თარიღის განახლებით. თქვენი ჩვენი სერვისების გამოყენების გაგრძელება ცვლილებების შემდეგ 
                  ნიშნავს განახლებული პოლიტიკის მიღებას.
                </p>
              </section>

              <section className="mb-4">
                <h2>12. დაგვიკავშირდით</h2>
                <p>
                  თუ თქვენ გაქვთ რაიმე კითხვა, შეშფოთება ან მოთხოვნა ამ კონფიდენციალურობის პოლიტიკის ან 
                  ჩვენი მონაცემთა პრაქტიკის შესახებ, გთხოვთ დაგვიკავშირდეთ:
                </p>
                <ul className="list-unstyled">
                  <li><strong>ელ. ფოსტა:</strong> support@syncorbai.com</li>
                  <li><strong>მისამართი:</strong> SyncOrb-AI, თბილისი, საქართველო</li>
                </ul>
              </section>
            </>
          )}

          {currentLang === 'ru' && (
            <>
              <section className="mb-4">
                <h2>1. Введение</h2>
                <p>
                  В SyncOrb-AI мы привержены защите вашей конфиденциальности и обеспечению безопасности 
                  вашей личной информации. Эта Политика конфиденциальности объясняет, как мы собираем, 
                  используем, раскрываем и защищаем вашу информацию при использовании наших услуг.
                </p>
              </section>

              <section className="mb-4">
                <h2>2. Информация, которую мы собираем</h2>
                <h3 className="h5 mt-3">Личная информация</h3>
                <p>Мы можем собирать личную информацию, которую вы нам предоставляете, включая:</p>
                <ul>
                  <li>Имя и контактную информацию (электронная почта, номер телефона)</li>
                  <li>Учетные данные и информацию профиля</li>
                  <li>Платежную и расчетную информацию</li>
                  <li>Предпочтения в общении</li>
                </ul>

                <h3 className="h5 mt-3">Информация об использовании</h3>
                <p>Мы автоматически собираем определенную информацию о вашем устройстве и о том, как вы взаимодействуете с нашими услугами:</p>
                <ul>
                  <li>Информация об устройстве (IP-адрес, тип браузера, операционная система)</li>
                  <li>Данные об использовании (посещенные страницы, используемые функции, время, проведенное)</li>
                  <li>Файлы cookie и аналогичные технологии отслеживания</li>
                  <li>Файлы журналов и данные аналитики</li>
                </ul>

                <h3 className="h5 mt-3">Данные для обучения ИИ</h3>
                <p>
                  Когда вы используете наши сервисы на базе ИИ, мы можем собирать данные для улучшения наших 
                  моделей и сервисов. Эти данные анонимизируются и агрегируются где это возможно.
                </p>
              </section>

              <section className="mb-4">
                <h2>3. Как мы используем вашу информацию</h2>
                <p>Мы используем собранную информацию для следующих целей:</p>
                <ul>
                  <li>Для предоставления, поддержки и улучшения наших услуг</li>
                  <li>Для обработки транзакций и отправки соответствующей информации</li>
                  <li>Для ответа на ваши запросы и предоставления поддержки клиентам</li>
                  <li>Для отправки административной информации и обновлений</li>
                  <li>Для обучения и улучшения наших моделей и алгоритмов ИИ</li>
                  <li>Для обнаружения, предотвращения и устранения технических проблем и угроз безопасности</li>
                  <li>Для соблюдения юридических обязательств и обеспечения соблюдения наших условий</li>
                  <li>Для анализа шаблонов использования и оптимизации пользовательского опыта</li>
                </ul>
              </section>

              <section className="mb-4">
                <h2>4. Обмен и раскрытие данных</h2>
                <p>Мы можем делиться вашей информацией в следующих обстоятельствах:</p>
                <ul>
                  <li><strong>Поставщики услуг:</strong> С внешними поставщиками, которые выполняют услуги от нашего имени</li>
                  <li><strong>Передача бизнеса:</strong> В связи со слияниями, приобретениями или продажей активов</li>
                  <li><strong>Юридические требования:</strong> Когда это требуется по закону или для защиты наших прав</li>
                  <li><strong>С вашего согласия:</strong> Когда вы явно разрешаете нам делиться вашей информацией</li>
                </ul>
                <p>Мы не продаем вашу личную информацию третьим лицам.</p>
              </section>

              <section className="mb-4">
                <h2>5. Безопасность данных</h2>
                <p>
                  Мы применяем соответствующие технические и организационные меры для защиты вашей личной 
                  информации от несанкционированного доступа, изменения, раскрытия или уничтожения. Эти 
                  меры включают:
                </p>
                <ul>
                  <li>Шифрование данных при передаче и хранении</li>
                  <li>Регулярные оценки и аудиты безопасности</li>
                  <li>Контроль доступа и механизмы аутентификации</li>
                  <li>Обучение сотрудников практикам защиты данных</li>
                </ul>
                <p>
                  Однако ни один метод передачи через интернет не является на 100% безопасным, и мы не можем 
                  гарантировать абсолютную безопасность.
                </p>
              </section>

              <section className="mb-4">
                <h2>6. Хранение данных</h2>
                <p>
                  Мы храним вашу личную информацию столько, сколько необходимо для выполнения целей, 
                  изложенных в настоящей Политике конфиденциальности, если более длительный период хранения 
                  не требуется или не разрешен законом. Когда нам больше не нужна ваша информация, мы безопасно 
                  удалим или анонимизируем ее.
                </p>
              </section>

              <section className="mb-4">
                <h2>7. Ваши права и выбор</h2>
                <p>В зависимости от вашего местоположения, у вас могут быть следующие права:</p>
                <ul>
                  <li><strong>Доступ:</strong> Запросить доступ к вашей личной информации</li>
                  <li><strong>Исправление:</strong> Запросить исправление неточных или неполных данных</li>
                  <li><strong>Удаление:</strong> Запросить удаление вашей личной информации</li>
                  <li><strong>Переносимость:</strong> Запросить копию ваших данных в портативном формате</li>
                  <li><strong>Возражение:</strong> Возражать против определенной обработки вашей информации</li>
                  <li><strong>Отказ:</strong> Отписаться от маркетинговых коммуникаций</li>
                </ul>
                <p>Чтобы воспользоваться этими правами, пожалуйста, свяжитесь с нами, используя информацию ниже.</p>
              </section>

              <section className="mb-4">
                <h2>8. Файлы cookie и технологии отслеживания</h2>
                <p>
                  Мы используем файлы cookie и аналогичные технологии для улучшения вашего опыта, анализа 
                  использования и предоставления персонализированного контента. Вы можете управлять настройками 
                  файлов cookie через свой браузер, но отключение файлов cookie может повлиять на функциональность 
                  наших услуг.
                </p>
              </section>

              <section className="mb-4">
                <h2>9. Международные передачи данных</h2>
                <p>
                  Ваша информация может быть передана и обработана в странах, отличных от страны вашего 
                  проживания. Мы обеспечиваем наличие соответствующих мер защиты для защиты вашей информации 
                  в соответствии с настоящей Политикой конфиденциальности и применимыми законами.
                </p>
              </section>

              <section className="mb-4">
                <h2>10. Конфиденциальность детей</h2>
                <p>
                  Наши услуги не предназначены для лиц младше 13 лет (или 16 лет в Европейской экономической зоне). 
                  Мы сознательно не собираем личную информацию от детей. Если вы считаете, что мы собрали информацию 
                  от ребенка, пожалуйста, немедленно свяжитесь с нами.
                </p>
              </section>

              <section className="mb-4">
                <h2>11. Изменения в Политике конфиденциальности</h2>
                <p>
                  Мы можем время от времени обновлять эту Политику конфиденциальности. Мы уведомим вас о любых 
                  существенных изменениях, разместив новую политику на этой странице и обновив дату "Последнее 
                  обновление". Ваше дальнейшее использование наших услуг после изменений означает принятие 
                  обновленной политики.
                </p>
              </section>

              <section className="mb-4">
                <h2>12. Свяжитесь с нами</h2>
                <p>
                  Если у вас есть какие-либо вопросы, опасения или запросы относительно этой Политики 
                  конфиденциальности или наших методов работы с данными, пожалуйста, свяжитесь с нами:
                </p>
                <ul className="list-unstyled">
                  <li><strong>Эл. почта:</strong> support@syncorbai.com</li>
                  <li><strong>Адрес:</strong> SyncOrb-AI, Тбилиси, Грузия</li>
                </ul>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
